import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormBuilderService } from '../../../../services/form-builder.service';
import {
  EIremboFormlyFieldTypes,
  EIremboFormlyFormStateKeys,
  EIremboFormlyFieldSubTypes,
  alphaNanoIdUtil,
} from '@irembo-andela/irembogov3-common';
import {
  getMaxLengthValidatorFormConfig,
  getMinLengthValidatorFormConfig,
  getPatternValidatorFormConfig,
  getRequiredValidatorFormConfig,
} from '../form-fields-formly-validation-field-generator.util';
import { GetEnumToLabelAndValueOptions } from '../../../form-builder-field-comparator-dropdown-options.utils';
import {
  FormBuilderFieldsWithDataSet,
  FormBuilderFieldsWithOptions,
} from '../../../../models/form-builder-display-comparator.enum';
import {
  IBasicFieldLabelAndDetails,
  IBasicLabelValuePair,
} from '../../../../models/basic-label-value-pair.model';
import {
  checkForInputWithOptionsAllowedComparators,
  FETCHED_DATA_FIELD_TYPES,
} from '../../form-fields-formly-display/form-fields-formly-display-field-generator.util';
import { TFormlyFieldKey } from '../../../../models/formly-key.type';
import {
  EAvailableConditionalValidations,
  EConditionalPropertyType,
  EFormBuilderConditionalFieldComparator,
  EFormBuilderConditionComparator,
} from '../../../../models/form-fields-formly-conditional-validation.model';
import { NonDataElementFormlyFieldTypes } from '../../../../models/irembo-formly-non-data-field-types.enum';
import { TAvailableConditionalValidations } from '../../../../models/form-fields-formly-validation.model';
import { generateErrorMappingFields } from '../../form-field-formly-types-main-configurations/generic-datafetch-field-type.configuration';

/** Conditionl Field Validations */
export const getConditionalValidationsFormConfig = (
  validationTypes: TAvailableConditionalValidations[],
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig => {
  const conditionValidationField: FormlyFieldConfig = {
    key: 'conditionalValidations',
    props: {
      label: 'Conditions',
      collapsible: true,
    },
    expressions: {
      'props.collapsed': 'field?.props?.badgeText === undefined',
    },
  };
  if (validationTypes.length < 1) {
    conditionValidationField.props = {
      hidden: true,
    };
    return conditionValidationField;
  }

  conditionValidationField.fieldGroup = [];

  validationTypes.forEach((type: TAvailableConditionalValidations) => {
    if (type === EAvailableConditionalValidations.required) {
      conditionValidationField.fieldGroup?.push(
        getConditionalRequiredValidatorFormConfig(formBuilderServiceInstance)
      );
    }
    if (type === EAvailableConditionalValidations.minLength) {
      conditionValidationField.fieldGroup?.push(
        getConditionalMinLengthValidatorFormConfig(formBuilderServiceInstance)
      );
    }
    if (type === EAvailableConditionalValidations.maxLength) {
      conditionValidationField.fieldGroup?.push(
        getConditionalMaxLengthValidatorFormConfig(formBuilderServiceInstance)
      );
    }
    if (type === EAvailableConditionalValidations.patternValidation) {
      conditionValidationField.fieldGroup?.push(
        getConditionalPatternValidatorFormConfig(formBuilderServiceInstance)
      );
    }
  });

  return conditionValidationField;
};

// Conditional Required
const getConditionalRequiredValidatorFormConfig = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig => {
  const field: FormlyFieldConfig = {
    key: EAvailableConditionalValidations.required,
    type: EIremboFormlyFieldTypes['formly-group'],
    props: {
      label: 'Required',
      placeholder: 'Configure Required Condition',
      collapsible: true,
      required: false,
    },
  };
  const requiredValidationField: FormlyFieldConfig = {
    ...getRequiredValidatorFormConfig(),
    key: 'validation',
    defaultValue: true,
    expressions: {
      'props.hideField': 'true',
      'props.label':
        'field?.formControl?.value ? "Will be required" : "Will not be required"',
    },
  };

  field.fieldGroup = [
    requiredValidationField,
    generateValidationAndConditionsField(formBuilderServiceInstance),
  ];

  return field;
};

// Conditional MinLength
const getConditionalMinLengthValidatorFormConfig = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig => {
  const field: FormlyFieldConfig = {
    key: EAvailableConditionalValidations.minLength,
    type: EIremboFormlyFieldTypes['formly-group'],
    props: {
      label: 'Min Length',
      placeholder: 'Configure minLength Condition',
      collapsible: true,
      required: false,
    },
  };
  const minLengthValidationField: FormlyFieldConfig = {
    ...getMinLengthValidatorFormConfig(),
    key: 'validation',
    defaultValue: undefined,
    props: {
      type: 'number',
      required: true,
      label: 'Min Character Length:',
    },
    validators: undefined,
  };

  field.fieldGroup = [
    minLengthValidationField,
    generateValidationAndConditionsField(formBuilderServiceInstance),
  ];
  return field;
};

// Conditional MaxLength
const getConditionalMaxLengthValidatorFormConfig = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig => {
  const maxLengthValidationField: FormlyFieldConfig = {
    ...getMaxLengthValidatorFormConfig(),
    key: 'validation',
    defaultValue: undefined,
    props: {
      type: 'number',
      required: true,
      label: 'Max Character Length:',
    },
    validators: undefined,
  };

  return {
    key: EAvailableConditionalValidations.maxLength,
    type: EIremboFormlyFieldTypes.customrepeater,
    props: {
      label: 'Max Length',
      placeholder: 'Configure maxLength Condition',
      collapsible: true,
      required: false,
    },
    fieldArray: {
      fieldGroup: [
        maxLengthValidationField,
        generateValidationAndConditionsField(formBuilderServiceInstance, true),
        ...generateCustomErrorMessageFields(),
      ],
      wrappers: ['panel-wrapper'],
      props: {
        label: 'MaxLength Scenarios',
        collapsible: true,
        required: false,
      },
      expressions: {
        'props.label': '"Scenario " + (Number(field?.key) + 1)',
      },
    },
    wrappers: ['panel-wrapper'],
  };
};

// Conditional MaxLength
const getConditionalPatternValidatorFormConfig = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig => {
  const patternValidationField: FormlyFieldConfig = {
    ...getPatternValidatorFormConfig(),
    key: 'validation',
    defaultValue: undefined,
    props: {
      required: true,
      label: 'Pattern Validation:',
    },
    // validators: undefined,
  };

  return {
    key: EAvailableConditionalValidations.patternValidation,
    type: EIremboFormlyFieldTypes.customrepeater,
    props: {
      label: 'Pattern',
      placeholder: 'Configure pattern Condition',
      collapsible: true,
      required: false,
    },
    fieldArray: {
      fieldGroup: [
        patternValidationField,
        generateValidationAndConditionsField(formBuilderServiceInstance, true),
        ...generateCustomErrorMessageFields(),
      ],
      wrappers: ['panel-wrapper'],
      props: {
        label: 'Pattern Scenarios',
        collapsible: true,
        required: false,
      },
      expressions: {
        'props.label': '"Scenario " + (Number(field?.key) + 1)',
      },
    },
    wrappers: ['panel-wrapper'],
  };
};

// Util validations
export const getConditionalUtilValidatorsFormConfig = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig => {
  const configuredField: FormlyFieldConfig | null =
    formBuilderServiceInstance.configureFieldAction$.value;

  const isDataFetchField = FETCHED_DATA_FIELD_TYPES.includes(
    <EIremboFormlyFieldTypes>configuredField?.type
  );

  if (
    !(
      NonDataElementFormlyFieldTypes.includes(
        <EIremboFormlyFieldTypes>configuredField?.type
      ) || isDataFetchField
    )
  ) {
    return {
      key: '_blank',
      hide: true,
    };
  }

  const validationNamefield: FormlyFieldConfig = {
    key: 'validationName',
    type: EIremboFormlyFieldTypes.input,
    props: {
      label: 'Validation Name',
      placeholder: 'Enter valdiation name',
      required: true,
      readonly: true,
      hideField: true,
    },
    hooks: {
      onInit: (field?: FormlyFieldConfig) => {
        if (!field?.formControl?.value) {
          field?.formControl?.setValue(`UtilValidation_${alphaNanoIdUtil(5)}`);
        }
      },
    },
  };
  const validationMessageField: FormlyFieldConfig = {
    key: 'validationMessage',
    type: EIremboFormlyFieldTypes.input,
    props: {
      label: 'Validation message',
      placeholder: 'Enter valdiation message',
      required: true,
    },
  };

  const fieldArray: FormlyFieldConfig = {
    props: {
      label: 'Validation',
    },
    fieldGroup: [
      validationNamefield,
      validationMessageField,
      {
        key: 'condition',
        props: {
          label: 'Validation',
          required: true,
        },
        fieldGroup: [
          ...generateConditionalValidationConditions(
            formBuilderServiceInstance,
            'must be'
          ),
        ],
      },
    ],
    expressions: {
      'props.label':
        'model?.validationMessage ? (model?.validationMessage?.length >= 35 ? "*" + model?.validationMessage?.substring(0, 34) + "..." : "*" + model?.validationMessage)  : "Validation"',
    },
  };

  presetDefaultValuesAndDisableSelectedUtilConfigFields(
    fieldArray,
    isDataFetchField,
    configuredField
  );

  return {
    key: 'utilValidations',
    props: {
      label: 'Response Validations',
      collapsible: true,
      template: 'displayRules',
      title: 'Validation',
      collapsed: true,
    },
    wrappers: ['service-validation-rule-wrapper'],
    type: EIremboFormlyFieldTypes.customrepeater,
    fieldArray,
  };
};

const presetDefaultValuesAndDisableSelectedUtilConfigFields = (
  fieldArray: FormlyFieldConfig,
  isDataFetchField: boolean,
  configuredField: FormlyFieldConfig | null
) => {
  const property1FormlyField: FormlyFieldConfig | undefined =
    fieldArray.fieldGroup
      ?.find(x => x.key === 'condition')
      ?.fieldGroup?.find(x => x.key === 'property1');
  const property1PropertyType = property1FormlyField?.fieldGroup?.find(
    x => x.key === 'propertyType'
  );
  const property1FormStateValuePath = property1FormlyField?.fieldGroup?.find(
    x => x.key === 'propertyFormStateValuePath'
  );
  const property1FormStateKey = property1FormStateValuePath?.fieldGroup?.find(
    x => x.key === 'formStateKey'
  );
  const property1FieldKey = property1FormStateValuePath?.fieldGroup?.find(
    x => x.key === 'fieldKey'
  );
  if (isDataFetchField && property1FormlyField) {
    if (property1PropertyType) {
      property1PropertyType.defaultValue =
        EConditionalPropertyType.formStateValue;
      property1PropertyType.props = {
        ...property1PropertyType.props,
        disabled: true,
        hideField: true,
      };
      property1PropertyType.expressions = {
        'props.hideField': 'true',
      };
    }

    if (property1FormStateKey) {
      property1FormStateKey.defaultValue =
        EIremboFormlyFormStateKeys.FETCHED_DATA;
      property1FormStateKey.props = {
        ...property1FormStateKey.props,
        disabled: true,
      };
    }

    if (property1FieldKey) {
      property1FieldKey.defaultValue = configuredField?.key;
      property1FieldKey.props = {
        ...property1FieldKey.props,
        disabled: true,
      };
    }
  }
};

export const generateValidationAndConditionsField = (
  formBuilderServiceInstance: FormBuilderService,
  conditionsRequired = false
): FormlyFieldConfig => {
  return {
    key: 'conditions',
    type: EIremboFormlyFieldTypes.customrepeater,
    className: 'form-builder-display-rules',
    props: {
      placeholder: 'When conditions apply',
      required: conditionsRequired,
      addText: 'Add Condition',
      hideLabel: true,
      title: 'Condition',
      template: 'displayRules',
    },
    fieldArray: {
      fieldGroup: generateConditionalValidationConditions(
        formBuilderServiceInstance
      ),
    },
  };
};

const generateConditionalValidationConditions = (
  formBuilderServiceInstance: FormBuilderService,
  comparatorVerb = 'is'
): FormlyFieldConfig[] => {
  const configuredField: FormlyFieldConfig | null =
    formBuilderServiceInstance.configureFieldAction$.getValue();

  const fetchedDataFieldsFormFieldLabelAndDetails: IBasicLabelValuePair<string>[] =
    [];

  const otherFormFieldLabelAndDetails: IBasicLabelValuePair<
    IBasicFieldLabelAndDetails<TFormlyFieldKey>
  >[] = [];

  if (
    FETCHED_DATA_FIELD_TYPES.includes(
      <EIremboFormlyFieldTypes>configuredField?.type
    )
  ) {
    fetchedDataFieldsFormFieldLabelAndDetails.push({
      label: configuredField?.props?.label ?? '',
      value: <string>configuredField?.key ?? '',
    });
  }

  formBuilderServiceInstance
    .getAllFormFieldLabelAndDetails(true, true, [
      EIremboFormlyFieldTypes.customcascadingdropdowns,
    ])
    .forEach((fieldDetails: IBasicFieldLabelAndDetails<TFormlyFieldKey>) => {
      if (
        FETCHED_DATA_FIELD_TYPES.includes(
          <EIremboFormlyFieldTypes>fieldDetails.fieldType
        )
      ) {
        fetchedDataFieldsFormFieldLabelAndDetails.push({
          label: fieldDetails.label,
          value: fieldDetails?.fieldKey ?? '',
        });
      }

      otherFormFieldLabelAndDetails.push({
        label: fieldDetails.label,
        value: fieldDetails,
      });
    });

  const property1Field: FormlyFieldConfig =
    generateConditionalValidationConditionsPropertyField(
      formBuilderServiceInstance,
      'property1',
      'Item 1',
      otherFormFieldLabelAndDetails,
      fetchedDataFieldsFormFieldLabelAndDetails
    );
  const property2Field: FormlyFieldConfig =
    generateConditionalValidationConditionsPropertyField(
      formBuilderServiceInstance,
      'property2',
      'Item 2',
      otherFormFieldLabelAndDetails,
      fetchedDataFieldsFormFieldLabelAndDetails,
      true
    );

  return [
    {
      key: 'condition',
      type: EIremboFormlyFieldTypes.customdropdown,
      props: {
        hideHeader: true,
        placeholder: 'AND or OR',
        required: false,
        options: GetEnumToLabelAndValueOptions(EFormBuilderConditionComparator),
        bindLabel: 'label',
        bindValue: 'value',
      },
      expressions: {
        'props.required': (field: FormlyFieldConfig) => {
          return !(
            field?.parent?.key == '0' || field?.parent?.key == 'condition'
          );
        },
        hide: (field: FormlyFieldConfig) => {
          return (
            field?.parent?.key === '0' || field?.parent?.key === 'condition'
          );
        },
      },
    },
    property1Field,
    {
      key: 'comparator',
      type: EIremboFormlyFieldTypes.select,
      props: {
        hideHeader: true,
        placeholder: 'select comparator',
        required: true,
        options: getComparatorOptionsLabelAndValues(comparatorVerb),
      },
      hooks: {
        onInit: (field: FormlyFieldConfig | undefined) => {
          field?.formControl?.valueChanges?.subscribe((value?: string) => {
            if (
              [
                EFormBuilderConditionalFieldComparator.IS_BOOLEAN_TRUE,
                EFormBuilderConditionalFieldComparator.IS_VALID,
                EFormBuilderConditionalFieldComparator.IS_UNDEFINED,
                EFormBuilderConditionalFieldComparator.IS_DEFINED,
              ].includes(<EFormBuilderConditionalFieldComparator>value)
            ) {
              field?.form?.get('property2.propertyType')?.setValue('self');
            }
          });
        },
      },
      expressions: {
        'props.options': (field: FormlyFieldConfig) => {
          const allOptions: IBasicLabelValuePair<string>[] =
            getComparatorOptionsLabelAndValues(comparatorVerb);
          if (
            field.parent?.model?.property1?.propertyType ===
            EConditionalPropertyType.formStateValue
          ) {
            return allOptions.filter(
              option =>
                option.value !== EFormBuilderConditionalFieldComparator.IS_VALID
            );
          }

          const options = allOptions.filter(
            option =>
              ![
                EFormBuilderConditionalFieldComparator.IS_UNDEFINED,
                EFormBuilderConditionalFieldComparator.IS_DEFINED,
              ].includes(<EFormBuilderConditionalFieldComparator>option.value)
          );

          const property1FieldType: EIremboFormlyFieldTypes =
            field.parent?.model?.property1?.propertyField?.field?.fieldType;

          if (!property1FieldType) {
            return options;
          }

          if (
            FormBuilderFieldsWithOptions.includes(property1FieldType) ||
            FormBuilderFieldsWithDataSet.includes(property1FieldType) ||
            [
              EIremboFormlyFieldTypes.customidinput,
              EIremboFormlyFieldTypes.customphonenumber,
              EIremboFormlyFieldTypes.textarea,
            ].includes(property1FieldType)
          ) {
            const allowedOptions: IBasicLabelValuePair<string>[] = [];
            options.forEach(option => {
              if (
                [
                  EFormBuilderConditionalFieldComparator.IS_EQUAL_TO,
                  EFormBuilderConditionalFieldComparator.IS_NOT_EQUAL_TO,
                  EFormBuilderConditionalFieldComparator.IS_VALID,
                ].includes(
                  option.value as EFormBuilderConditionalFieldComparator
                ) ||
                (property1FieldType === EIremboFormlyFieldTypes.checkbox &&
                  option.value ===
                    EFormBuilderConditionalFieldComparator.IS_BOOLEAN_TRUE)
              ) {
                allowedOptions.push(option);
              }
            });
            return allowedOptions;
          } else if (property1FieldType === EIremboFormlyFieldTypes.input) {
            const allowedOptions: IBasicLabelValuePair<string>[] = [];
            options.forEach(option => {
              if (
                [
                  EFormBuilderConditionalFieldComparator.IS_EQUAL_TO,
                  EFormBuilderConditionalFieldComparator.IS_NOT_EQUAL_TO,
                  EFormBuilderConditionalFieldComparator.IS_ABOVE,
                  EFormBuilderConditionalFieldComparator.IS_BELOW,
                  EFormBuilderConditionalFieldComparator.IS_VALID,
                ].includes(
                  option.value as EFormBuilderConditionalFieldComparator
                )
              ) {
                allowedOptions.push(option);
              }
            });
            return allowedOptions;
          } else {
            return options;
          }
        },
        'model.comparator': 'model?.comparator',
      },
    },
    {
      key: 'strictMode',
      type: EIremboFormlyFieldTypes.checkbox,
      defaultValue: false,
      props: {
        hideHeader: true,
        label: 'Strict compare mode',
      },
      expressions: {
        hide: (field: FormlyFieldConfig) =>
          ![
            EFormBuilderConditionalFieldComparator.IS_EQUAL_TO,
            EFormBuilderConditionalFieldComparator.IS_NOT_EQUAL_TO,
          ].includes(field?.parent?.model?.comparator),
      },
    },
    {
      key: 'useOrEqualTo',
      type: EIremboFormlyFieldTypes.checkbox,
      defaultValue: false,
      props: {
        hideHeader: true,
        label: 'or Equal to',
      },
      expressions: {
        hide: (field: FormlyFieldConfig) =>
          ![
            EFormBuilderConditionalFieldComparator.IS_ABOVE,
            EFormBuilderConditionalFieldComparator.IS_BELOW,
          ].includes(field?.parent?.model?.comparator),
      },
    },
    property2Field,
  ];
};

const getComparatorOptionsLabelAndValues = (comparatorVerb = 'is') => {
  return GetEnumToLabelAndValueOptions(
    EFormBuilderConditionalFieldComparator
  ).map(option => {
    return {
      label: `${comparatorVerb} ${option.label}`,
      value: option.value,
    };
  });
};

const generateConditionalValidationConditionsPropertyField = (
  formBuilderServiceInstance: FormBuilderService,
  key: string,
  label: string,
  otherFormFieldLabelAndDetails: IBasicLabelValuePair<
    IBasicFieldLabelAndDetails<TFormlyFieldKey>
  >[],
  fetchedDataFieldsFormFieldLabelAndDetails: IBasicLabelValuePair<string>[],
  isProperty2 = false
): FormlyFieldConfig => {
  const compareOptions = [
    {
      label: 'Compared against a field Value',
      value: EConditionalPropertyType.fieldValue,
    },
    {
      label: 'Compare against Form State Value',
      value: EConditionalPropertyType.formStateValue,
    },
  ];
  if (isProperty2) {
    compareOptions.unshift({
      label: 'Compare against Item 1',
      value: EConditionalPropertyType.self,
    });
  }

  const field: FormlyFieldConfig = {
    key,
    type: EIremboFormlyFieldTypes['formly-group'],
    defaultValue: EConditionalPropertyType.fieldValue,
    props: {
      label,
    },
    // wrappers: ['panel-wrapper'],
  };
  field.fieldGroup = [
    {
      key: 'propertyType',
      type: EIremboFormlyFieldTypes.customdropdown,
      defaultValue: EConditionalPropertyType.fieldValue,
      props: {
        hideHeader: true,
        placeholder: 'Compared against..',
        required: true,
        bindValue: 'value',
        bindLabel: 'label',
        options: compareOptions,
      },
      expressions: {
        'props.hideField': (field: FormlyFieldConfig) =>
          field?.parent?.key === 'property2' &&
          [
            EFormBuilderConditionalFieldComparator.IS_BOOLEAN_TRUE,
            EFormBuilderConditionalFieldComparator.IS_UNDEFINED,
            EFormBuilderConditionalFieldComparator.IS_DEFINED,
            EFormBuilderConditionalFieldComparator.IS_VALID,
          ].includes(field?.parent?.parent?.model?.comparator),
      },
    },
    {
      key: 'propertyFormStateValuePath',
      type: EIremboFormlyFieldTypes['formly-group'],
      props: {
        hideHeader: true,
        required: false,
      },
      fieldGroup: [
        {
          key: 'formStateKey',
          type: EIremboFormlyFieldTypes.select,
          props: {
            hideHeader: true,
            placeholder: 'Select form State Key',
            required: true,
            bindValue: 'value',
            options: [
              {
                label: 'Fetched Data',
                value: EIremboFormlyFormStateKeys.FETCHED_DATA,
              },
              {
                label: 'Application State',
                value: EIremboFormlyFormStateKeys.APPLICATION_STATE,
              },
              {
                label: 'Applicant Type',
                value: EIremboFormlyFormStateKeys.APPLICANT_TYPE,
              },
              {
                label: 'Application Locale',
                value: EIremboFormlyFormStateKeys.LOCALE,
              },
            ],
          },
          expressions: {
            'props.description': (field: FormlyFieldConfig) => {
              let description: string | undefined = undefined;
              switch (field?.formControl?.value) {
                case EIremboFormlyFormStateKeys.APPLICANT_BUSINESS_ID: {
                  description =
                    'Stores the business ID for business applicants';
                  break;
                }
                case EIremboFormlyFormStateKeys.APPLICANT_BUSINESS_NAME: {
                  description = 'The business name for business applicants';
                  break;
                }
                case EIremboFormlyFormStateKeys.APPLICANT_TYPE: {
                  description = 'The applicant type';
                  break;
                }
                case EIremboFormlyFormStateKeys.APPLICATION_STATE: {
                  description = 'The application state';
                  break;
                }
                case EIremboFormlyFormStateKeys.FETCHED_DATA: {
                  description =
                    'Fetched data received by fields that fetch external data against thier field key';
                  break;
                }
                case EIremboFormlyFormStateKeys.FETCHED_OPTIONS_DATA: {
                  description =
                    'Fetched list of fetched options received by custom dropdowns with data fetch';
                  break;
                }
                case EIremboFormlyFormStateKeys.LOCALE: {
                  description = 'The locale used in the application';
                  break;
                }
                default:
                  description = '';
              }
              return description;
            },
          },
        },
        {
          key: 'fieldKey',
          type: EIremboFormlyFieldTypes.customdropdown,
          props: {
            hideHeader: true,
            placeholder: 'Select the field key in the formState data',
            required: true,
            bindLabel: 'label',
            bindValue: 'value',
            options: fetchedDataFieldsFormFieldLabelAndDetails,
          },
          expressions: {
            hide: `!(model?.formStateKey === '${EIremboFormlyFormStateKeys.FETCHED_DATA}')`,
          },
        },
        {
          key: 'valuePath',
          type: EIremboFormlyFieldTypes.input,
          props: {
            hideHeader: true,
            placeholder: 'Value path',
            required: true,
            description:
              'value path to access value e.g `sex` in {sex: "MALE"} or `users?.[0].sex` in  {users: [sex: "MALE"]}',
          },
          expressions: {
            hide: `!(model?.formStateKey === '${EIremboFormlyFormStateKeys.FETCHED_DATA}')`,
          },
        },
      ],
      expressions: {
        hide: (field: FormlyFieldConfig) =>
          field?.parent?.model?.propertyType !==
          EConditionalPropertyType.formStateValue,
      },
    },
    {
      key: 'propertyField',
      type: EIremboFormlyFieldTypes['formly-group'],
      fieldGroup: [
        {
          key: 'field',
          type: EIremboFormlyFieldTypes.select,
          props: {
            hideHeader: true,
            placeholder: 'Select Field',
            required: true,
            compareWith: (
              o1: IBasicLabelValuePair<TFormlyFieldKey>,
              o2: IBasicLabelValuePair<TFormlyFieldKey>
            ) => {
              return o1?.value === o2?.value;
            },
            options: otherFormFieldLabelAndDetails,
          },
          expressions: {
            'props.options': (field?: FormlyFieldConfig) => {
              if (!isProperty2) {
                return otherFormFieldLabelAndDetails;
              }
              return otherFormFieldLabelAndDetails.filter(
                detail =>
                  detail.value?.fieldRefPath !==
                  field?.parent?.parent?.parent?.model?.property1?.propertyField
                    ?.field?.fieldRefPath
              );
            },
          },
        },
      ],
      expressions: {
        hide: (field: FormlyFieldConfig) =>
          field?.parent?.model?.propertyType !==
          EConditionalPropertyType.fieldValue,
      },
    },
  ];

  if (isProperty2) {
    const propertyValuesField: FormlyFieldConfig = {
      key: 'propertyValues',
      type: EIremboFormlyFieldTypes['formly-group'],
      props: {
        hideHeader: true,
        placeholder: 'Compared Value',
      },
      fieldGroup: [
        {
          key: 'isValue',
          type: EIremboFormlyFieldTypes.input,
          props: {
            hideHeader: true,
            placeholder: 'Compared Value',
            required: true,
          },
          expressions: {
            hide: (field: FormlyFieldConfig) =>
              field?.model?.valueAsNumber ||
              [
                EFormBuilderConditionalFieldComparator.IS_BOOLEAN_TRUE,
                EFormBuilderConditionalFieldComparator.IS_UNDEFINED,
                EFormBuilderConditionalFieldComparator.IS_DEFINED,
                EFormBuilderConditionalFieldComparator.IS_VALID,
              ].includes(field?.parent?.parent?.parent?.model?.comparator) ||
              FormBuilderFieldsWithOptions.includes(
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldType
              ) ||
              FormBuilderFieldsWithDataSet.includes(
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldType
              ),
          },
        },
        {
          key: 'isValue',
          type: EIremboFormlyFieldTypes.input,
          props: {
            hideHeader: true,
            placeholder: 'Compared Value (Number value)',
            type: 'number',
            required: true,
          },
          expressions: {
            hide: (field: FormlyFieldConfig) =>
              !field?.model?.valueAsNumber ||
              [
                EFormBuilderConditionalFieldComparator.IS_BOOLEAN_TRUE,
                EFormBuilderConditionalFieldComparator.IS_UNDEFINED,
                EFormBuilderConditionalFieldComparator.IS_DEFINED,
                EFormBuilderConditionalFieldComparator.IS_VALID,
              ].includes(field?.parent?.parent?.parent?.model?.comparator) ||
              FormBuilderFieldsWithOptions.includes(
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldType
              ) ||
              FormBuilderFieldsWithDataSet.includes(
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldType
              ),
          },
        },
        {
          key: 'isValue',
          type: EIremboFormlyFieldTypes.customdropdownpaginated,
          props: {
            hideHeader: true,
            placeholder: 'Select',
            options: [],
            useBaseUrl: true,
          },
          expressions: {
            hide: (field: FormlyFieldConfig) =>
              [
                EFormBuilderConditionalFieldComparator.IS_BOOLEAN_TRUE,
                EFormBuilderConditionalFieldComparator.IS_UNDEFINED,
                EFormBuilderConditionalFieldComparator.IS_DEFINED,
                EFormBuilderConditionalFieldComparator.IS_VALID,
              ].includes(field?.parent?.parent?.parent?.model?.comparator) ||
              !FormBuilderFieldsWithDataSet.includes(
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldType
              ) ||
              FormBuilderFieldsWithOptions.includes(
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldType
              ) ||
              field?.parent?.parent?.parent?.model?.property1?.propertyField
                ?.field?.fieldSubType ===
                EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH,
            'props.required': (field: FormlyFieldConfig) =>
              checkForInputWithOptionsAllowedComparators(
                field?.parent?.parent?.parent?.model?.comparator
              ),
            'props.dataset': (field: FormlyFieldConfig) => {
              if (
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldType ===
                EIremboFormlyFieldTypes.customdropdownpaginated
              ) {
                return formBuilderServiceInstance.findFormlyFieldConfigInFormByKey(
                  field?.parent?.parent?.parent?.model?.property1?.propertyField
                    ?.field?.fieldKey
                )?.props?.['dataset'];
              }
            },
            'props.bindLabel': (field: FormlyFieldConfig) => {
              if (
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldType ===
                EIremboFormlyFieldTypes.customdropdownpaginated
              ) {
                return formBuilderServiceInstance.findFormlyFieldConfigInFormByKey(
                  field?.parent?.parent?.parent?.model?.property1?.propertyField
                    ?.field?.fieldKey
                )?.props?.['bindLabel'];
              }
            },
            'props.bindValue': (field: FormlyFieldConfig) => {
              return formBuilderServiceInstance.findFormlyFieldConfigInFormByKey(
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldKey
              )?.props?.['bindValue'];
            },
          },
        },
        {
          key: 'isValue',
          type: EIremboFormlyFieldTypes.select,
          props: {
            hideHeader: true,
            placeholder: 'Select',
            options: [],
          },
          expressions: {
            hide: (field: FormlyFieldConfig) =>
              !(
                FormBuilderFieldsWithOptions.includes(
                  field?.parent?.parent?.parent?.model?.property1?.propertyField
                    ?.field?.fieldType
                ) &&
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldSubType !==
                  EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH &&
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldSubType !==
                  EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
              ) ||
              [
                EFormBuilderConditionalFieldComparator.IS_BOOLEAN_TRUE,
                EFormBuilderConditionalFieldComparator.IS_UNDEFINED,
                EFormBuilderConditionalFieldComparator.IS_DEFINED,
                EFormBuilderConditionalFieldComparator.IS_VALID,
              ].includes(field?.parent?.parent?.parent?.model?.comparator) ||
              FormBuilderFieldsWithDataSet.includes(
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldType
              ),
            'props.required': (field: FormlyFieldConfig) =>
              checkForInputWithOptionsAllowedComparators(
                field?.parent?.parent?.parent?.model?.comparator
              ),
            'props.options': (field: FormlyFieldConfig) => {
              const property1Fieldkey =
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldKey;

              const property1FieldType =
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldType;

              const fieldOptions =
                formBuilderServiceInstance.findFormlyFieldConfigInFormByKey(
                  property1Fieldkey
                )?.props?.options;

              if (
                !fieldOptions &&
                property1FieldType === EIremboFormlyFieldTypes.checkbox
              ) {
                return [
                  {
                    label: 'Checked',
                    value: true,
                  },
                  {
                    label: 'Unchecked',
                    value: false,
                  },
                ];
              }
              return fieldOptions;
            },
          },
        },
        {
          key: 'isValue',
          type: EIremboFormlyFieldTypes.customdropdown,
          props: {
            hideHeader: true,
            placeholder: 'Select',
            options: [],
            useBaseUrl: true,
            subType: EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH,
          },
          expressions: {
            hide: (field: FormlyFieldConfig) =>
              field?.parent?.parent?.parent?.model?.property1?.propertyField
                ?.field?.fieldSubType !==
              EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH,
            'props.required': (field: FormlyFieldConfig) =>
              checkForInputWithOptionsAllowedComparators(
                field?.parent?.parent?.parent?.model?.comparator
              ),
            'props.dataset': (field: FormlyFieldConfig) => {
              if (
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldSubType ===
                EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH
              ) {
                return formBuilderServiceInstance.findFormlyFieldConfigInFormByKey(
                  field?.parent?.parent?.parent?.model?.property1?.propertyField
                    ?.field?.fieldKey
                )?.props?.['dataset'];
              }
            },
            'props.bindLabel': (field: FormlyFieldConfig) => {
              return formBuilderServiceInstance.findFormlyFieldConfigInFormByKey(
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldKey
              )?.props?.['bindLabel'];
            },
            'props.bindValue': (field: FormlyFieldConfig) => {
              return formBuilderServiceInstance.findFormlyFieldConfigInFormByKey(
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldKey
              )?.props?.['bindValue'];
            },
            'props.code': (field: FormlyFieldConfig) => {
              return formBuilderServiceInstance.findFormlyFieldConfigInFormByKey(
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldKey
              )?.props?.['code'];
            },
          },
        },
        {
          key: 'isValue',
          type: EIremboFormlyFieldTypes.customdropdown,
          props: {
            hideHeader: true,
            placeholder: 'Select',
            options: [],
            useBaseUrl: true,
            subType: EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET,
          },
          expressions: {
            hide: (field: FormlyFieldConfig) =>
              [
                EFormBuilderConditionalFieldComparator.IS_BOOLEAN_TRUE,
                EFormBuilderConditionalFieldComparator.IS_UNDEFINED,
                EFormBuilderConditionalFieldComparator.IS_DEFINED,
                EFormBuilderConditionalFieldComparator.IS_VALID,
              ].includes(field?.parent?.parent?.parent?.model?.comparator) ||
              field?.parent?.parent?.parent?.model?.property1?.propertyField
                ?.field?.fieldSubType !==
                EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET,
            'props.required': (field: FormlyFieldConfig) =>
              checkForInputWithOptionsAllowedComparators(
                field?.parent?.parent?.parent?.model?.comparator
              ),
            'props.dataset': (field: FormlyFieldConfig) => {
              if (
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldSubType ===
                EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
              ) {
                return formBuilderServiceInstance.findFormlyFieldConfigInFormByKey(
                  field?.parent?.parent?.parent?.model?.property1?.propertyField
                    ?.field?.fieldKey
                )?.props?.['dataset'];
              }
            },
            'props.bindLabel': (field: FormlyFieldConfig) => {
              return formBuilderServiceInstance.findFormlyFieldConfigInFormByKey(
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldKey
              )?.props?.['bindLabel'];
            },
            'props.bindValue': (field: FormlyFieldConfig) => {
              return formBuilderServiceInstance.findFormlyFieldConfigInFormByKey(
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldKey
              )?.props?.['bindValue'];
            },
          },
        },
        {
          key: 'valueAsNumber',
          type: EIremboFormlyFieldTypes.checkbox,
          defaultValue: false,
          props: {
            hideHeader: true,
            label: 'Value as Number',
          },
          expressions: {
            hide: (field: FormlyFieldConfig) =>
              [
                EFormBuilderConditionalFieldComparator.IS_BOOLEAN_TRUE,
                EFormBuilderConditionalFieldComparator.IS_UNDEFINED,
                EFormBuilderConditionalFieldComparator.IS_DEFINED,
                EFormBuilderConditionalFieldComparator.IS_VALID,
              ].includes(field?.parent?.parent?.parent?.model?.comparator) ||
              FormBuilderFieldsWithOptions.includes(
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldType
              ) ||
              FormBuilderFieldsWithDataSet.includes(
                field?.parent?.parent?.parent?.model?.property1?.propertyField
                  ?.field?.fieldType
              ),
          },
        },
        {
          key: 'booleanValue',
          type: EIremboFormlyFieldTypes.customdropdown,
          props: {
            hideHeader: true,
            placeholder: 'Select boolean value',
            required: true,
            options: [
              {
                value: true,
                label: 'True',
              },
              {
                value: false,
                label: 'False',
              },
            ],
            multiple: false,
            bindValue: 'value',
            bindLabel: 'label',
          },
          expressions: {
            hide: (field?: FormlyFieldConfig) => {
              return (
                field?.parent?.parent?.parent?.model?.comparator !==
                EFormBuilderConditionalFieldComparator.IS_BOOLEAN_TRUE
              );
            },
          },
        },
        {
          key: 'isUndefined',
          type: EIremboFormlyFieldTypes.customdropdown,
          defaultValue: true,
          props: {
            hideHeader: true,
            readonly: true,
            required: true,
            options: [
              {
                value: true,
                label: 'Undefined',
              },
              {
                value: false,
                label: 'Defined',
              },
            ],
            multiple: false,
            bindValue: 'value',
            bindLabel: 'label',
          },
          expressions: {
            hide: (field?: FormlyFieldConfig) => {
              return (
                field?.parent?.parent?.parent?.model?.comparator !==
                EFormBuilderConditionalFieldComparator.IS_UNDEFINED
              );
            },
          },
        },
        {
          key: 'isDefined',
          type: EIremboFormlyFieldTypes.customdropdown,
          defaultValue: true,
          props: {
            hideHeader: true,
            readonly: true,
            required: true,
            options: [
              {
                value: true,
                label: 'Defined',
              },
              {
                value: false,
                label: 'Undefined',
              },
            ],
            multiple: false,
            bindValue: 'value',
            bindLabel: 'label',
          },
          expressions: {
            hide: (field?: FormlyFieldConfig) => {
              return (
                field?.parent?.parent?.parent?.model?.comparator !==
                EFormBuilderConditionalFieldComparator.IS_DEFINED
              );
            },
          },
        },
        {
          key: 'validityValue',
          type: EIremboFormlyFieldTypes.customdropdown,
          props: {
            hideHeader: true,
            placeholder: 'Select validity value',
            required: true,
            options: [
              {
                value: true,
                label: 'VALID',
              },
              {
                value: false,
                label: 'INVALID',
              },
            ],
            multiple: false,
            bindValue: 'value',
            bindLabel: 'label',
          },
          expressions: {
            hide: (field?: FormlyFieldConfig) => {
              return (
                field?.parent?.parent?.parent?.model?.comparator !==
                EFormBuilderConditionalFieldComparator.IS_VALID
              );
            },
          },
        },
      ],
      expressions: {
        hide: (field: FormlyFieldConfig) =>
          field?.parent?.model?.propertyType !== EConditionalPropertyType.self,
      },
    };
    field.expressions = {
      ...(field?.expressions ?? {}),
      'props.hideField': (field: FormlyFieldConfig) => {
        return [
          EFormBuilderConditionalFieldComparator.IS_UNDEFINED,
          EFormBuilderConditionalFieldComparator.IS_DEFINED,
        ].includes(
          <EFormBuilderConditionalFieldComparator>(
            field?.parent?.model?.comparator
          )
        );
      },
    };
    field.fieldGroup.push(propertyValuesField);
  }

  return field;
};

const generateCustomErrorMessageFields = (): FormlyFieldConfig[] => {
  return [
    {
      key: 'useCustomErrorMessage',
      type: EIremboFormlyFieldTypes.checkbox,
      defaultValue: false,
      props: {
        hideHeader: true,
        label: 'Use Custom error message',
      },
    },
    {
      key: 'errorMessage',
      type: EIremboFormlyFieldTypes.input,
      props: {
        hideHeader: true,
        label: 'Custom error message',
        placeholder: 'Enter custom error message',
      },
      expressions: {
        hide: '!model?.useCustomErrorMessage',
      },
    },
  ];
};

export const getErrorMappingValidatorFormConfig = (): FormlyFieldConfig => {
  return {
    key: 'errorMapping',
    type: EIremboFormlyFieldTypes['formly-group'],
    wrappers: ['service-validation-rule-wrapper'],
    props: {
      label: 'Error Mapping',
      dragDisabled: true,
      collapsible: true,
      collapsed: true,
    },
    fieldGroup: generateErrorMappingFields(),
    expressions: {},
  };
};
